<template>
  <v-flex>
    <v-layout row wrap>
      <v-flex xs12>
        <h2 class="primary--text pb-2">Summary</h2>
        <p>Change the name, description, menu visibility of a case type here.</p>
      </v-flex>
      <v-flex
        xs12
        sm6
        :class="{'pr-0 pb-4 mt-3': $vuetify.breakpoint.smAndDown, 'mt-3 pr-4 pb-0': $vuetify.breakpoint.mdAndUp}">
        <v-text-field
          v-model="caseTypeName"
          label="Name"
          box
        ></v-text-field>
        <v-text-field
          v-model="caseTypeDisplayName"
          label="Display name"
          box
          v-if="existingCaseType"
        ></v-text-field>
        <v-text-field v-model="caseTypeDescription" label="Description" box></v-text-field>
        <v-text-field
          v-model="caseType.code"
          label="Short code"
          box
          disabled
          persistent-hint
          hint="Set when the template is created from the name and cannot be changed. Use Display name to rename."
          v-if="existingCaseType"
        ></v-text-field>
        <v-switch
          color="secondary"
          label="Display this case template in main navigation"
          v-model="caseTypeCoreTemplate"
          class="mb-3"
        ></v-switch>
        <v-flex mb-4>
          <div class="caption opacity-54 mb-1 primary--text">Template Icon</div>
          <v-layout v-if="caseTypeIcon.code" column wrap align-left>
            <v-layout px-1 mx-1>
              <font-awesome-icon size="6x" :icon="[caseTypeIcon.set, caseTypeIcon.code]"></font-awesome-icon>
            </v-layout>
            <v-layout ml-3 pl-2>
              <span class="icon-name">{{caseTypeIcon.code}}</span>
            </v-layout>
          </v-layout>
          <v-layout v-if="!caseTypeIcon.code" column wrap align-left>
            <v-layout px-4 mx-4 py-2>
              <font-awesome-icon size="sm" :icon="['fas', 'exclamation-triangle']"></font-awesome-icon>
            </v-layout>
            <span class="icon-name">No Icon Selected</span>
          </v-layout>
          <v-btn
            color="secondary"
            class="ml-0 mt-3"
            @click="updateIcons();iconDialog = true"
            style="display:block"
          >
            <v-icon small class="mr-2">edit</v-icon>Edit Icon
          </v-btn>
          <v-dialog v-model="iconDialog" width="100%" max-width="1200px">
            <v-card class="pa-4">
              <v-card-title class="ma-0 mb-2 pa-0">
                <h2>Choose your icon</h2>
              </v-card-title>
              <v-card-text class="ma-0 mb-1 pa-0">
                <p>Just click on an icon to select.</p>
              </v-card-text>
              <v-divider class="mb-4"></v-divider>
              <v-layout>
                <v-flex xs3 class="hidden-xs-only">
                  <p class="mb-3">Filter by catgories</p>
                  <v-checkbox
                    class="mb-0 mt-0"
                    color="secondary"
                    label="Regular"
                    v-model="iconFilters.regular"
                    @change="updateIcons()"
                  ></v-checkbox>
                  <v-checkbox
                    class="mb-0 mt-0"
                    color="secondary"
                    label="Light"
                    v-model="iconFilters.light"
                    @change="updateIcons()"
                  ></v-checkbox>
                  <v-checkbox
                    class="mb-0 mt-0"
                    color="secondary"
                    label="Solid"
                    v-model="iconFilters.solid"
                    @change="updateIcons()"
                  ></v-checkbox>
                  <v-checkbox
                    class="mb-0 mt-0"
                    color="secondary"
                    label="Brands"
                    v-model="iconFilters.brands"
                    @change="updateIcons()"
                  ></v-checkbox>
                </v-flex>
                <v-divider vertical class="mr-4 hidden-xs-only"></v-divider>
                <v-flex xs12 sm9>
                  <v-text-field
                    class="pa-0 ma-0 body-1 mt-0"
                    label="Search for an icon..."
                    v-model="iconFilterSearch"
                    box
                    clearable
                    clear-icon="cancel"
                  >
                    <template slot="append">
                      <v-icon>search</v-icon>
                    </template>
                  </v-text-field>
                  <h2>Choose from 1000 icons...</h2>
                  <v-flex class="icons" v-if="icons.length > 0">
                    <div
                      v-for="(icon, iconIndex) in icons"
                      :key="iconIndex"
                      :class="((iconSelection === icon) ? 'icon active' : 'icon')"
                    >
                      <div class="inner">
                        <font-awesome-icon
                          size="lg"
                          @click="selectIcon(icon)"
                          :icon="[icon.set, icon.code]"
                        ></font-awesome-icon>
                        <span style="display:block; width:100%;">{{icon.code}}</span>
                      </div>
                    </div>
                  </v-flex>
                </v-flex>
              </v-layout>
            </v-card>
          </v-dialog>
        </v-flex>
        <v-flex xs12 v-if="!existingCaseType">
          <h2 class="primary--text pb-2">Schema</h2>
          <p>
            Add in your template code here. If you need some help check out our
            <a
              class="text-link"
              href="https://documentation.caseblocks.com/"
            >
              Schema Documentation
              <v-icon color="secondary" small>launch</v-icon>
            </a>
          </p>
          <div class="schema-editor-shell">
            <edit-code-pane
              :code="schema"
              language="yaml"
              height="100%"
              :schema-intellisense="true"
              :schema-completions="schemaCompletions"
              v-on:update-code="updateSchema">
            </edit-code-pane>
          </div>
        </v-flex>
      </v-flex>
    </v-layout>
  </v-flex>
</template>

<script>
import EditCodePane from "@/components/shared/editCodePane.vue"
import { library } from "@fortawesome/fontawesome-svg-core"
import "@/lib/fontAwesomeLibrary"
import { mapState } from "vuex"
import yaml from "js-yaml"
import Helpers from "@/helpers.js"

export default {
  name: "case-config-summary",
  components: { EditCodePane },
  props: { caseType: { type: Object, required: true } },
  data() {
    return {
      iconDialog: false,
      iconFilterSearch: "",
      iconSelection: "",
      iconFilters: {
        regular: false,
        light: false,
        solid: false,
        brands: false
      },
      currentIcon: { code: "", set: "" },
      icons: [],
      cmOptionsSchema: {
        tabSize: 4,
        theme: "base16-light",
        lineNumbers: true,
        line: true
      }
    }
  },
  computed: {
    ...mapState({
      teams: state => state.teams.all,
      allCaseTypes: state => (state.caseTypes && state.caseTypes.all) ? state.caseTypes.all : [],
      configTab() {
        return this.$route.params.config_tab
      }
    }),
    schemaCompletions() {
      const types = this.allCaseTypes || []
      const codes = types.map(function (ct) { return ct && ct.code }).filter(Boolean)
      return {
        caseTypeCodes: codes,
        scriptNames: [],
        uniqueIdentifierField: ""
      }
    },
    existingCaseType() {
      return (
        this.caseType &&
        this.caseType.id !== undefined &&
        this.caseType.id !== null
      )
    },
    caseTypeName: {
      get() {
        if (this.caseType) {
          return this.caseType.name
        }
        return ""
      },
      set(name) {
        if (name !== this.caseType.name) {
          this.$emit("update-summary-data", { key: "name", value: name })
          this.updateSchemaName(name)
        }
      }
    },
    caseTypeDisplayName: {
      get() {
        if (this.caseType) {
          return this.caseType.displayName
        }
        return ""
      },
      set(displayName) {
        if (displayName !== this.caseType.displayName) {
          this.$emit("update-summary-data", {
            key: "displayName",
            value: displayName
          })
        }
      }
    },
    caseTypeDescription: {
      get() {
        if (this.caseType) {
          return this.caseType.description
        }
        return ""
      },
      set(description) {
        if (description !== this.caseType.description) {
          this.$emit("update-summary-data", {
            key: "description",
            value: description
          })
        }
      }
    },
    caseTypeIcon: {
      get() {
        if (this.currentIcon.code !== "") {
          return this.currentIcon
        } else {
          if (this.caseType) {
            return { set: this.caseType.iconSet, code: this.caseType.iconCode }
          }
        }
        return undefined
      },
      set(icon) {
        if (icon !== this.caseType.icon) {
          this.currentIcon = icon
          this.$emit("update-summary-data", {
            key: "iconSet",
            value: icon.set
          })
          this.$emit("update-summary-data", {
            key: "iconCode",
            value: icon.code
          })
        }
      }
    },
    caseTypeCoreTemplate: {
      get() {
        if (this.caseType) {
          return this.caseType.coreTemplate
        }
        return undefined
      },
      set(template) {
        if (template !== this.caseType.coreTemplate) {
          this.$emit("update-summary-data", {
            key: "coreTemplate",
            value: template
          })
        }
      }
    },
    schema: {
      get() {
        if (this.caseType.currentSchema) {
          const schema = yaml.safeDump(this.caseType.currentSchema)
          const schemaLines = schema.split("\n")
          let formattedSchema = ""
          schemaLines.forEach(function(line) {
            if (!line.includes("null") && !line.includes("__typename:")) {
              formattedSchema += line + "\n"
            }
          }) // Remove all null values added by GraphQL
          return formattedSchema
        }
        return undefined
      },
      set(schema) {
        this.$store.dispatch("updateSchema", schema)
        this.$emit("update-summary-data", { key: "schema", value: schema })
      }
    }
  },
  watch: {
    caseType() {
      if (this.caseType) {
        return (this.currentIcon = {
          code: this.caseType.iconCode,
          set: this.caseType.iconSet
        })
      }
    },
    iconFilterSearch() {
      this.updateIcons()
    }
  },
  methods: {
    updateSchema(update) {
      this.schema = update
    },
    updateSchemaName(newName) {
      const schemaObj = this.caseType.currentSchema
      schemaObj.name = newName
      const internalFieldset = schemaObj.fieldsets.filter(fieldset => fieldset.name === "internal_references")[0]
      if (internalFieldset) {
        const refField = internalFieldset.fields.filter(field => field.sequence)[0]
        if (refField) {
          refField.name = `${Helpers.formatSnakeCase(newName)}_ref`
        }
      }
      const schema = yaml.safeDump(schemaObj)
      const schemaLines = schema.split("\n")
      let formattedSchema = ""
      schemaLines.forEach(function(line) {
        if (!line.includes("null") && !line.includes("__typename:")) {
          formattedSchema += line + "\n"
        }
      }) // Remove all null values added by GraphQL
      this.schema = formattedSchema
    },
    updateIcons() {
      const icons = []
      const noFilter =
        !this.iconFilters.regular &&
        !this.iconFilters.light &&
        !this.iconFilters.solid &&
        !this.iconFilters.brands

      if ((this.iconFilters.regular || noFilter) && library.definitions && library.definitions.far) {
        Object.keys(library.definitions.far).forEach(function(icon) {
          icons.push({ name: icon, set: "far", code: icon, type: "regular" })
        })
      }
      if ((this.iconFilters.light || noFilter) && library.definitions && library.definitions.fal) {
        Object.keys(library.definitions.fal).forEach(function(icon) {
          icons.push({ name: icon, set: "fal", code: icon, type: "light" })
        })
      }
      if ((this.iconFilters.solid || noFilter) && library.definitions && library.definitions.fas) {
        Object.keys(library.definitions.fas).forEach(function(icon) {
          icons.push({ name: icon, set: "fas", code: icon, type: "solid" })
        })
      }
      if ((this.iconFilters.brands || noFilter) && library.definitions && library.definitions.fab) {
        Object.keys(library.definitions.fab).forEach(function(icon) {
          icons.push({ name: icon, set: "fab", code: icon, type: "brands" })
        })
      }
      if (this.iconFilterSearch) {
        this.icons = icons.filter(
          icon =>
            icon && icon.name.includes(this.iconFilterSearch.toLowerCase())
        )
      } else {
        this.icons = icons
      }
    },
    selectIcon(icon) {
      this.caseTypeIcon = icon
      this.iconDialog = false
      this.icons = []
    }
  }
}
</script>

<style>
.schema-editor-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 50vh;
  max-height: 50vh;
  min-height: 240px;
  overflow: hidden;
}

.schema-editor-shell .monaco-editor-frame,
.schema-editor-shell >>> .monaco-editor-frame {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}
.icons {
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  margin-left: -5px;
  margin-top: 20px;
}

.icons .icon {
  flex: 0 0 127px;
  text-align: center;
  box-sizing: border-box;
  margin: 0 5px;
  margin-top: 10px;
  border-radius: 2px;
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  background: white;
}

.icons .icon .v-icon {
  width: 100%;
  padding: 10px 0;
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.icons .icon span {
  padding: 10px 0 10px 0;
  line-height: 1.2em;
  font-size: 0.9em;
  opacity: 0.54;
}

.icons .icon:hover .inner {
  cursor: pointer;
  box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2),
    0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 14px 0px rgba(0, 0, 0, 0.12) !important;
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.icons .icon:hover .v-icon,
.icons .icon.active .v-icon {
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  color: white !important;
  background: rgb(13, 148, 96); /* Old browsers */
  background: -moz-linear-gradient(
    top,
    rgba(13, 148, 96, 1) 0%,
    rgba(9, 102, 66, 1) 100%
  ); /* FF3.6-15 */
  background: -webkit-linear-gradient(
    top,
    rgba(13, 148, 96, 1) 0%,
    rgba(9, 102, 66, 1) 100%
  ); /* Chrome10-25,Safari5.1-6 */
  background: linear-gradient(
    to bottom,
    rgba(13, 148, 96, 1) 0%,
    rgba(9, 102, 66, 1) 100%
  ); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
}
</style>
